/**
 * 卡路里计算工具函数
 */

/**
 * 计算基础代谢率 (BMR) - 使用Mifflin-St Jeor公式
 * @param {number} weight - 体重(kg)
 * @param {number} height - 身高(cm)
 * @param {number} age - 年龄
 * @param {string} gender - 性别 ('male' 或 'female')
 * @returns {number} BMR (kcal/day)
 */
export const calculateBMR = (weight, height, age, gender) => {
    if (!weight || !height || !age || !gender) {
        return 0;
    }
    
    // Mifflin-St Jeor公式
    if (gender === 'male') {
        return Math.round(10 * weight + 6.25 * height - 5 * age + 5);
    } else {
        return Math.round(10 * weight + 6.25 * height - 5 * age - 161);
    }
};

/**
 * 获取活动系数
 * @param {string} activityLevel - 活动水平
 * @returns {number} 活动系数
 */
export const getActivityMultiplier = (activityLevel) => {
    const activityMultipliers = {
        'Sedentary': 1.2,           // 久坐
        'Lightly Active': 1.375,    // 轻度活动
        'Moderately Active': 1.55,  // 中度活动
        'Very Active': 1.725        // 高度活动
    };
    
    return activityMultipliers[activityLevel] || 1.2;
};

/**
 * 计算总日能量消耗 (TDEE)
 * @param {number} bmr - 基础代谢率
 * @param {string} activityLevel - 活动水平
 * @returns {number} TDEE (kcal/day)
 */
export const calculateTDEE = (bmr, activityLevel) => {
    const multiplier = getActivityMultiplier(activityLevel);
    return Math.round(bmr * multiplier);
};

/**
 * 计算减重目标卡路里
 * @param {number} tdee - 总日能量消耗
 * @param {number} currentWeight - 当前体重(kg)
 * @param {number} targetWeight - 目标体重(kg)
 * @returns {number} 目标卡路里摄入
 */
export const calculateTargetCalories = (tdee, currentWeight, targetWeight) => {
    if (!currentWeight || !targetWeight) {
        return tdee;
    }
    
    const weightDifference = currentWeight - targetWeight;
    
    // 根据减重目标调整卡路里
    if (weightDifference > 0) {
        // 减重：每周减重0.5-1kg，每天减少500-1000卡路里
        const weeklyDeficit = Math.min(weightDifference * 0.5, 1); // 每周最多减1kg
        const dailyDeficit = Math.round(weeklyDeficit * 1000 / 7); // 转换为每日赤字
        return Math.max(tdee - dailyDeficit, 1200); // 最低1200卡路里
    } else {
        // 增重或维持
        return tdee;
    }
};

const formatMetricValue = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
        return '0';
    }

    const fixedValue = numericValue.toFixed(1);
    return fixedValue.endsWith('.0') ? fixedValue.slice(0, -2) : fixedValue;
};

const formatImperialValue = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? String(Math.round(numericValue)) : '0';
};

const formatProfileWeight = (weightData) => {
    if (!weightData) {
        return '0kg';
    }

    if (weightData.unit === 'lbs') {
        return `${formatImperialValue(weightData.weight)}lbs`;
    }

    return `${formatMetricValue(weightData.weight)}kg`;
};

const formatProfileHeight = (heightData) => {
    if (!heightData) {
        return '0cm';
    }

    if (heightData.unit === 'ft') {
        const feet = formatImperialValue(heightData.height1);
        const inches = Number(heightData.height2);

        if (!Number.isFinite(inches) || Math.round(inches) === 0) {
            return `${feet}ft`;
        }

        return `${feet}ft ${formatImperialValue(inches)}in`;
    }

    return `${formatMetricValue(heightData.height1)}cm`;
};

const formatProfile = (formData) => {
    if (!formData) {
        return '0kg, 0cm';
    }

    return `${formatProfileWeight(formData.currentWeight)}, ${formatProfileHeight(formData.height)}`;
};

/**
 * 根据用户数据计算个性化卡路里目标
 * @param {Object} formData - 问卷 FormData 实例
 * @returns {Object} 计算结果
 */
export const calculatePersonalizedCalories = (formData) => {
    if (!formData) {
        return {
            bmr: 0,
            tdee: 0,
            targetCalories: 0,
            profile: '0kg, 0cm',
            dietaryPreference: 'Balanced'
        };
    }
    
    // 获取用户数据
    const weight = parseFloat(formData.currentWeight?.getWeightWithUnitKG()) || 0;
    const height = parseFloat(formData.height?.getHeightWithUnitM() * 100) || 0; // 转换为cm
    const age = parseInt(formData.age) || 0;
    const gender = formData.gender || 'male';
    const activityLevel = formData.activityLevel || 'Sedentary';
    const targetWeight = parseFloat(formData.targetWeight?.getWeightWithUnitKG()) || weight;
    
    // 计算BMR
    const bmr = calculateBMR(weight, height, age, gender);
    
    // 计算TDEE
    const tdee = calculateTDEE(bmr, activityLevel);
    
    // 计算目标卡路里
    const targetCalories = calculateTargetCalories(tdee, weight, targetWeight);
    
    // 计算时统一用公制，展示时保留用户原始单位
    const profile = formatProfile(formData);
    
    // 获取饮食偏好
    const dietaryPreferences = ['Balanced', 'Low carbs', 'Vegetarian', 'Vegan', 'Mediterranean', 'Paleo', 'High protein', 'Low fat'];
    const dietaryPreference = dietaryPreferences[formData.getDietaryPreference()] || 'Balanced';
    
    return {
        bmr,
        tdee,
        targetCalories,
        profile,
        dietaryPreference
    };
};
